<template>
  <div v-if="loading" class="w-full">
    <!-- Spinner Loading -->
    <div v-if="type === 'spinner'" class="flex items-center justify-center py-12">
      <div class="text-center">
        <v-progress-circular 
          indeterminate 
          color="primary" 
          size="64"
          :width="4"
        ></v-progress-circular>
        <p class="mt-4 text-gray-600 text-sm">{{ message || $t('reports.loading.default') || 'Loading...' }}</p>
      </div>
    </div>

    <!-- Skeleton Loading -->
    <div v-else-if="type === 'skeleton'" class="w-full">
      <!-- Table Skeleton -->
      <div v-if="variant === 'table'" class="bg-white rounded-lg shadow-sm overflow-hidden">
        <!-- Header Skeleton -->
        <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div class="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
          </div>
        </div>
        
        <!-- Table Body Skeleton -->
        <div class="divide-y divide-gray-200">
          <div v-for="i in rows" :key="i" class="px-6 py-4">
            <div class="flex items-center space-x-4">
              <div v-for="j in columns" :key="j" class="flex-1">
                <div class="h-4 bg-gray-200 rounded animate-pulse" :style="{ width: getColumnWidth(j) }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Card Skeleton -->
      <div v-else-if="variant === 'card'" class="bg-white rounded-lg shadow-sm p-6">
        <div class="space-y-4">
          <div class="h-6 bg-gray-200 rounded w-3/4 animate-pulse"></div>
          <div class="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
          <div class="h-4 bg-gray-200 rounded w-2/3 animate-pulse"></div>
        </div>
      </div>

      <!-- Chart Skeleton -->
      <div v-else-if="variant === 'chart'" class="bg-white rounded-lg shadow-sm p-6">
        <div class="h-6 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div class="h-64 bg-gray-200 rounded animate-pulse"></div>
      </div>

      <!-- Default Skeleton -->
      <div v-else class="bg-white rounded-lg shadow-sm p-6">
        <div class="space-y-3">
          <div class="h-4 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
          <div class="h-4 bg-gray-200 rounded w-4/6 animate-pulse"></div>
        </div>
      </div>
    </div>

    <!-- Progress Loading -->
    <div v-else-if="type === 'progress'" class="w-full">
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-gray-700">{{ message || $t('reports.loading.processing') }}</span>
          <span class="text-sm text-gray-500">{{ progress }}%</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div 
            class="bg-primary-600 h-2 rounded-full transition-all duration-300 ease-out"
            :style="{ width: `${progress}%` }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReportLoading',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'spinner',
      validator: (value) => ['spinner', 'skeleton', 'progress'].includes(value)
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'table', 'card', 'chart'].includes(value)
    },
    message: {
      type: String,
      default: null
    },
    progress: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0 && value <= 100
    },
    rows: {
      type: Number,
      default: 5
    },
    columns: {
      type: Number,
      default: 4
    }
  },
  methods: {
    getColumnWidth(index) {
      // Generate different widths for visual variety
      const widths = ['25%', '30%', '20%', '25%', '15%', '35%', '20%', '30%'];
      return widths[index % widths.length];
    }
  }
};
</script>

<style scoped>
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}
</style> 